
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { useSimpleProfile } from "@/hooks/useSimpleProfile";
import { Moon, Sun, Monitor } from "lucide-react";

interface AppearanceSettingsProps {
  settings: any;
  onSettingsChange: (settings: any) => void;
}

const AppearanceSettings = ({ settings, onSettingsChange }: AppearanceSettingsProps) => {
  const { theme, effectiveTheme, setTheme } = useTheme();
  const { profile, updateProfile } = useSimpleProfile();

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    onSettingsChange({ theme: newTheme });
    
    // Update user preferences if profile exists
    if (profile) {
      updateProfile({
        preferences: {
          ...profile.preferences,
          theme: newTheme
        }
      });
    }
  };

  const handlePreferenceChange = (key: string, value: boolean) => {
    onSettingsChange({ [key]: value });
    
    // Update user preferences if profile exists
    if (profile) {
      updateProfile({
        preferences: {
          ...profile.preferences,
          [key]: value
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="text-foreground">Theme Settings</CardTitle>
          <CardDescription className="text-muted-foreground">
            Customize the appearance of your admin interface
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">Theme Mode</Label>
            <div className="flex space-x-2">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('light')}
                className={theme === 'light' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Sun className="h-4 w-4 mr-2" />
                Light
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('dark')}
                className={theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Moon className="h-4 w-4 mr-2" />
                Dark
              </Button>
              <Button
                variant={theme === 'system' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('system')}
                className={theme === 'system' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Monitor className="h-4 w-4 mr-2" />
                System
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Current effective theme: {effectiveTheme}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium text-foreground">Compact Mode</Label>
              <p className="text-xs text-muted-foreground">
                Reduce spacing and padding for a more dense layout
              </p>
            </div>
            <Switch
              checked={settings.compactMode || false}
              onCheckedChange={(checked) => handlePreferenceChange('compactMode', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium text-foreground">Animations</Label>
              <p className="text-xs text-muted-foreground">
                Enable smooth transitions and animations
              </p>
            </div>
            <Switch
              checked={settings.animations !== false}
              onCheckedChange={(checked) => handlePreferenceChange('animations', checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppearanceSettings;
